import { useContext, useMemo } from 'react'
import {
  BookingOrderItemDetailsContext,
  PurchaseOrderItemDetailsContext,
  useOrderDetails,
} from './contexts'
import { useQueryClient } from 'react-query'
import { useActiveOrderId, useCurrentOrder } from 'contexts/Order'
import { STATUS_SETTLED } from 'constants/orderStatuses'
import { map } from 'lodash'
import {
  FRIENDLY_TYPES,
  TYPE_APPOINTMENT,
  TYPE_AREA_BOOKING,
  TYPE_COURSE,
  TYPE_PACKAGE,
  TYPE_PRODUCT,
  TYPE_SESSION,
  TYPE_VOUCHER,
} from 'constants/offeringTypes'
import { useAddOrderItem } from 'api/OrderItems'
import { ApiErrorNotification, useNotificationContext } from 'ui/components/Notification'

/**
 * @deprecated
 */
export const useBookingOrderItemDetails = () => useContext(BookingOrderItemDetailsContext)

/**
 * @deprecated
 */
export const usePurchaseOrderItemDetails = () => useContext(PurchaseOrderItemDetailsContext)

export const useActiveOrder = () => {
  const queryClient = useQueryClient()
  const orderId = useActiveOrderId()

  // Get the order from the cached query
  const previousData = queryClient.getQueryData(['orders', orderId])
  const { data: order = {} } = previousData

  return order
}

export const useActiveOrderItems = () => {
  const { booking_items = [], purchase_items = [], package_items = [] } = useActiveOrder()

  return booking_items.concat(purchase_items).concat(package_items)
}

export const useActiveOrderIsSettled = () => {
  const { status } = useActiveOrder()

  return status === STATUS_SETTLED
}

export const useItemsByGuest = (orderItems = []) => {
  return useMemo(() => {
    return orderItems.reduce((byGuestIds, basketItem) => {
      const guestIds = map(basketItem.guests, 'id').sort().join(',')
      byGuestIds[guestIds] = byGuestIds[guestIds] || []
      byGuestIds[guestIds].push(basketItem)

      return byGuestIds
    }, {})
  }, [orderItems])
}

export const useAddItem = () => {
  const { setValues } = useOrderDetails()
  const { order: { id: orderId } = {} } = useCurrentOrder()
  const { mutateAsync: addOrderItem } = useAddOrderItem()
  const notification = useNotificationContext()

  const addItem = async (offeringSummary) => {
    await addOrderItem(
      [
        orderId,
        {
          offering_type: offeringSummary.offering_type,
          offering_id: offeringSummary.id,
          guest_ids: [],
        },
      ],
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={`${FRIENDLY_TYPES[offeringSummary.offering_type]} added`}
              description={`The ${
                FRIENDLY_TYPES[offeringSummary.offering_type]
              }.toLowerCase() was successfully added`}
              variant="success"
              autoDismiss
            />
          )
        },
        onError: (error) => {
          notification.notify(<ApiErrorNotification error={error} />)
        },
      }
    )
  }

  return (offering) => {
    switch (offering.offering_type) {
      case TYPE_APPOINTMENT:
        setValues({
          'appointmentWizard.editingAppointment': {},
          'appointmentWizard.appointmentTypeId': offering.id,
        })
        break

      case TYPE_AREA_BOOKING:
        setValues({
          'areaBookingWizard.editingAreaBooking': {},
          'areaBookingWizard.areaBookingTypeId': offering.id,
        })
        break

      case TYPE_COURSE:
        addItem(offering)
        break

      case TYPE_PACKAGE:
        setValues({
          'packageWizard.editingPackage': {},
          'packageWizard.packageTypeId': offering.id,
        })
        break

      case TYPE_PRODUCT:
        setValues({
          'productWizard.editingProduct': {},
          'productWizard.productTypeId': offering.id,
        })
        break

      case TYPE_SESSION:
        setValues({
          'sessionWizard.editingSession': {},
          'sessionWizard.sessionTypeId': offering.id,
        })
        break

      case TYPE_VOUCHER:
        setValues({
          'voucherWizard.editingVoucher': {},
          'voucherWizard.voucherTypeId': offering.id,
        })
        break
    }
  }
}
